import { useCookieValue } from '@react-hookz/web/useCookieValue/index.js';
import { createContext, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  TranslationsContext,
  TranslationsContextProps,
} from './translations.types';
import { getDetectedLanguage } from './translations.utils';

export const Context = createContext<TranslationsContext | null>(null);

export function useTranslationsContext() {
  const data = useContext(Context);

  if (!data) {
    throw new Error(
      'useTranslationsContext hook should be wrapped by <TranslationsProvider />'
    );
  }

  return data;
}

export function TranslationsProvider({
  children,
  supportedLanguages,
}: TranslationsContextProps) {
  const [cookieValue, setCookieValue] = useCookieValue('user-lang');
  const { t, i18n } = useTranslation();

  const handleLanguageChange = async (language: string) => {
    setCookieValue(language);
    await i18n.changeLanguage?.(language);
  };

  useEffect(() => {
    const { changeLanguage } = i18n;
    const language = getDetectedLanguage(cookieValue, supportedLanguages);

    void changeLanguage?.(language);
  }, [cookieValue, i18n, supportedLanguages]);

  useEffect(() => {
    if (i18n.resolvedLanguage) {
      document.documentElement.lang = i18n.resolvedLanguage;
    }
  }, [i18n.resolvedLanguage]);

  return (
    <Context.Provider
      value={{
        handleLanguageChange,
        resolvedLanguage: i18n.resolvedLanguage,
        t,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function withTranslationsProvider({
  children,
  ...props
}: TranslationsContextProps) {
  return <TranslationsProvider {...props}>{children}</TranslationsProvider>;
}
